import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import LanguageSelector from "../components/LanguageSelector";
import { translations } from "../i18n/translations";
import { api } from "../App";
import { compressImage } from "../utils/imageCompress";

/** Mini SVG preview of each letterhead template (used in template picker). */
const SettingsPage = ({ user, logout, lang, setLang }) => {
  const navigate = useNavigate();
  const t = (key) => translations[lang]?.[key] || translations['tr'][key] || key;
  
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState("");
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Firma/Genel Bilgiler
  const [companyData, setCompanyData] = useState({
    companyName: "",
    logo: "",
    website: "",
    address: "",
    phone: "",
    email: "",
    sender_email: "",
    // Antetli kağıt — user uploads the full A4 design, picks top/bottom padding.
    letterhead_image: "",
    letterhead_top_mm: 40,
    letterhead_bottom_mm: 25,
    // Gizli kar marjı (%) — her malzeme/işçilik birim fiyatına otomatik eklenir
    profit_margin: 0,
  });

  // Load saved settings on mount (prefer backend, fallback to localStorage)
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await api.get('/user/settings');
        if (!isMounted) return;
        if (res?.data && typeof res.data === 'object') {
          const merged = {
            companyName: res.data.companyName || "",
            logo: res.data.logo || "",
            website: res.data.website || "",
            address: res.data.address || "",
            phone: res.data.phone || "",
            email: res.data.email || "",
            sender_email: res.data.sender_email || "",
            letterhead_image: res.data.letterhead_image || "",
            letterhead_top_mm:
              typeof res.data.letterhead_top_mm === 'number' ? res.data.letterhead_top_mm : 40,
            letterhead_bottom_mm:
              typeof res.data.letterhead_bottom_mm === 'number' ? res.data.letterhead_bottom_mm : 25,
            profit_margin:
              typeof res.data.profit_margin === 'number' ? res.data.profit_margin : 0,
            tax_office: res.data.tax_office || "",
            tax_number: res.data.tax_number || "",
            trade_registry_no: res.data.trade_registry_no || "",
            mersis_no: res.data.mersis_no || "",
            bank_name: res.data.bank_name || "",
            iban: res.data.iban || "",
            account_holder: res.data.account_holder || "",
            stamp_signature: res.data.stamp_signature || "",
          };
          setCompanyData(merged);
          if (merged.logo) setLogoPreview(merged.logo);
          // Keep localStorage in sync as cache for offline / quick boot
          localStorage.setItem('nkt_company_settings', JSON.stringify(merged));
          return;
        }
      } catch (err) {
        // Backend unreachable — fall through to localStorage cache
      }
      const savedSettings = localStorage.getItem('nkt_company_settings');
      if (savedSettings && isMounted) {
        try {
          const parsed = JSON.parse(savedSettings);
          setCompanyData(parsed);
          if (parsed.logo) setLogoPreview(parsed.logo);
        } catch (_) {}
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('logoTooBig'));
      return;
    }

    try {
      toast.info(t('logoPreparing'));
      const { dataUrl } = await compressImage(file, { maxDim: 512, quality: 0.9 });
      if (!dataUrl) throw new Error("Boş çıktı");
      setLogoPreview(dataUrl);
      setCompanyData((p) => ({ ...p, logo: dataUrl }));
    } catch (err) {
      console.error("Logo compress error:", err);
      toast.error(t('logoProcessFailed') + (err.message || ""));
    } finally {
      e.target.value = "";
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    await persistSettings();
  };

  const persistSettings = async () => {
    setLoading(true);

    try {
      // Save to backend FIRST (primary storage so PDF/JPG export reads correct values).
      // Images are pre-compressed in their upload handlers to stay well under ingress limits.
      await api.post('/user/settings', companyData);

      // Mirror to localStorage as a quick-boot cache
      localStorage.setItem('nkt_company_settings', JSON.stringify(companyData));

      toast.success(t('settingsSaved'));
    } catch (error) {
      console.error('Settings save failed:', error);
      const status = error.response?.status;
      let detail = error.response?.data?.detail || error.message || 'Bilinmeyen hata';

      // Translate the common payload-too-large / blocked-by-proxy cases into a
      // user-friendly message instead of the raw "404 / Request failed".
      if (status === 413 || status === 404 || status === 0 || /Network Error|413/.test(detail)) {
        detail = t('imageTooLargeError');
      }

      toast.error("Ayarlar kaydedilemedi: " + detail);
      // Still mirror to localStorage so user doesn't lose their entries
      try { localStorage.setItem('nkt_company_settings', JSON.stringify(companyData)); } catch (_) {}
    } finally {
      setLoading(false);
    }
  };

  const removeLogo = () => {
    setLogoPreview("");
    setCompanyData({ ...companyData, logo: "" });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('passwordMismatch'));
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error(t('passwordTooShort'));
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success(t('passwordChangedSuccess'));
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('passwordChangeError'));
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar user={user} lang={lang} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
                  {t('settings') || 'Ayarlar'}
                </h1>
                <p className="text-slate-500 text-xs sm:text-sm">{t('settingsSubtitle')}</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <LanguageSelector currentLang={lang} onLanguageChange={setLang} />
              </div>
            </div>
          </div>
        </header>

        {/* Settings Content */}
        <main className="p-4 sm:p-6">
          <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
          
          {/* Info Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-amber-800 text-sm">
              {t('settingsInfoBanner')}
            </p>
          </div>

          {/* Genel Bilgiler / Company Info */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                {t('generalInfo')}
              </CardTitle>
              <CardDescription>
                {t('companyInfoDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveSettings} className="space-y-6">
                
                {/* Logo Section */}
                <div className="space-y-3">
                  <Label>{t('companyLogo')}</Label>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <div className="relative">
                        <img 
                          src={logoPreview} 
                          alt="Logo" 
                          className="w-24 h-24 object-contain border-2 border-amber-200 rounded-lg bg-white p-2"
                        />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50">
                        <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
                      />
                      <p className="text-xs text-slate-500 mt-1">{t('logoNote')}</p>
                    </div>
                  </div>
                </div>

                {/* Company Name & Website */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="companyName">{t('company')}</Label>
                    <Input
                      id="companyName"
                      value={companyData.companyName}
                      onChange={(e) => setCompanyData({...companyData, companyName: e.target.value})}
                      placeholder="NK Tasarım Dekorasyon"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website">{t('websiteLabel')}</Label>
                    <Input
                      id="website"
                      value={companyData.website}
                      onChange={(e) => setCompanyData({...companyData, website: e.target.value})}
                      placeholder="www.nktteklif.com"
                    />
                  </div>
                </div>

                {/* Phone & Email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">{t('phone')}</Label>
                    <Input
                      id="phone"
                      value={companyData.phone}
                      onChange={(e) => setCompanyData({...companyData, phone: e.target.value})}
                      placeholder="0532 346 89 20"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">{t('email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={companyData.email}
                      onChange={(e) => setCompanyData({...companyData, email: e.target.value})}
                      placeholder="info@nktteklif.com"
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <Label htmlFor="address">{t('address')}</Label>
                  <Input
                    id="address"
                    value={companyData.address}
                    onChange={(e) => setCompanyData({...companyData, address: e.target.value})}
                    placeholder="Kurtuluş Mh. 64016 Bina No:20 Kat:0 Daire No:1 Seyhan/Adana"
                  />
                </div>

                {/* Reply-To Email — teklif alıcısı 'Yanıtla' derse buraya düşer */}
                <div className="pt-6 border-t border-slate-200">
                  <Label htmlFor="sender_email" className="text-sm font-semibold text-slate-900">
                    {t('senderEmailLabel')}
                  </Label>
                  <p className="text-xs text-slate-500 mt-0.5 mb-2">
                    {t('senderEmailHelp')}
                  </p>
                  <Input
                    id="sender_email"
                    type="email"
                    value={companyData.sender_email}
                    onChange={(e) => setCompanyData({...companyData, sender_email: e.target.value})}
                    placeholder="ornek@gmail.com"
                    data-testid="sender-email-input"
                  />
                </div>

                {/* ===================== ANTETLİ KAĞIT (LETTERHEAD) ===================== */}
                <div className="pt-6 border-t border-slate-200">
                  <div className="mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">{t('letterheadTitle')}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {t('letterheadDesc')}
                    </p>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Left: Upload + Sliders */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium mb-2 block">{t('letterheadImageLabel')}</Label>
                        <Input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 15 * 1024 * 1024) {
                              toast.error(t('imageTooBig'));
                              return;
                            }
                            try {
                              toast.info(t('imagePreparing'));
                              // Compress to A4 aspect (longest edge 1754px, ~150 DPI) and
                              // JPEG q=0.85 — keeps the file under ~500KB even for HD
                              // photos. Without this, raw 5MB+ PNGs from phones turn into
                              // 7-10MB base64 payloads which Cloudflare ingress rejects (404/413).
                              const { dataUrl } = await compressImage(file, {
                                maxDim: 1754,
                                quality: 0.85,
                              });
                              if (!dataUrl) throw new Error("Boş çıktı");
                              setCompanyData((p) => ({ ...p, letterhead_image: dataUrl }));
                              toast.success(t('imageUploaded'));
                            } catch (err) {
                              console.error("Letterhead compress error:", err);
                              toast.error(t('imageProcessFailed') + ": " + (err.message || ""));
                            } finally {
                              // Reset input so the same file can be re-selected
                              e.target.value = "";
                            }
                          }}
                          data-testid="letterhead-upload"
                          className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
                        />
                        <p className="text-xs text-slate-500 mt-1">{t('letterheadNote')}</p>
                        {companyData.letterhead_image && (
                          <button
                            type="button"
                            onClick={() => setCompanyData((p) => ({ ...p, letterhead_image: "" }))}
                            className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
                            data-testid="remove-letterhead-btn"
                          >
                            {t('removeLetterhead')}
                          </button>
                        )}
                      </div>

                      <div>
                        <div className="flex justify-between mb-1">
                          <Label className="text-sm font-medium">{t('topMarginLabel')}</Label>
                          <span className="text-sm font-mono text-amber-700">
                            {Math.round(companyData.letterhead_top_mm)} mm
                          </span>
                        </div>
                        <input
                          type="range"
                          min="10"
                          max="120"
                          step="1"
                          value={companyData.letterhead_top_mm}
                          onChange={(e) =>
                            setCompanyData((p) => ({
                              ...p,
                              letterhead_top_mm: parseFloat(e.target.value),
                            }))
                          }
                          data-testid="letterhead-top-slider"
                          className="w-full accent-amber-600"
                          disabled={!companyData.letterhead_image}
                        />
                        <p className="text-xs text-slate-500">{t('topMarginDesc')}</p>
                      </div>

                      <div>
                        <div className="flex justify-between mb-1">
                          <Label className="text-sm font-medium">{t('bottomMarginLabel')}</Label>
                          <span className="text-sm font-mono text-amber-700">
                            {Math.round(companyData.letterhead_bottom_mm)} mm
                          </span>
                        </div>
                        <input
                          type="range"
                          min="10"
                          max="120"
                          step="1"
                          value={companyData.letterhead_bottom_mm}
                          onChange={(e) =>
                            setCompanyData((p) => ({
                              ...p,
                              letterhead_bottom_mm: parseFloat(e.target.value),
                            }))
                          }
                          data-testid="letterhead-bottom-slider"
                          className="w-full accent-amber-600"
                          disabled={!companyData.letterhead_image}
                        />
                        <p className="text-xs text-slate-500">{t('bottomMarginDesc')}</p>
                      </div>
                    </div>

                    {/* Right: A4 Preview (210×297, scaled to ~200×283) */}
                    <div className="flex justify-center">
                      <div
                        className="relative bg-white border border-slate-300 shadow-lg overflow-hidden"
                        style={{ width: '200px', height: '283px' }}
                        data-testid="letterhead-preview"
                      >
                        {companyData.letterhead_image ? (
                          <img
                            src={companyData.letterhead_image}
                            alt={t('letterheadTitle')}
                            className="absolute inset-0 w-full h-full"
                            style={{ objectFit: 'fill' }}
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-slate-300 text-xs text-center px-4">
                            {t('letterheadNotUploaded')}
                          </div>
                        )}
                        {/* Content area overlay (red dashed rect) */}
                        {(() => {
                          // 200px = 210mm, so 1mm ≈ 0.952px (vertical: 283/297 ≈ 0.953)
                          const pxPerMm = 200 / 210;
                          const sideMm = 20;
                          const left = sideMm * pxPerMm;
                          const right = sideMm * pxPerMm;
                          const top = companyData.letterhead_top_mm * (283 / 297);
                          const bottom = companyData.letterhead_bottom_mm * (283 / 297);
                          return (
                            <div
                              className="absolute border-2 border-dashed border-amber-500/70 bg-amber-100/20"
                              style={{
                                left: `${left}px`,
                                right: `${right}px`,
                                top: `${top}px`,
                                bottom: `${bottom}px`,
                              }}
                            >
                              <div className="text-[8px] text-amber-700 font-semibold p-1 leading-tight">
                                {t('textAreaLabel')}<br />
                                <span className="opacity-70">↕ {t('textAreaHint')}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                >
                  {loading ? t('saving') : t('saveSettingsBtn')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Kar Marjı Section */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                {t('profitMarginTitle')}
              </CardTitle>
              <CardDescription>
                {t('profitMarginDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="flex-1 max-w-xs">
                  <Label htmlFor="profitMargin">{t('profitMarginRateLabel')}</Label>
                  <div className="relative">
                    <Input
                      id="profitMargin"
                      data-testid="profit-margin-input"
                      type="number"
                      min="0"
                      max="500"
                      step="0.5"
                      value={companyData.profit_margin}
                      onChange={(e) =>
                        setCompanyData({
                          ...companyData,
                          profit_margin: e.target.value === "" ? 0 : Math.max(0, parseFloat(e.target.value) || 0),
                        })
                      }
                      placeholder="0"
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                  </div>
                </div>
                <Button
                  type="button"
                  data-testid="save-profit-margin-btn"
                  onClick={persistSettings}
                  disabled={loading}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {loading ? t('saving') : t('saveProfitMarginBtn')}
                </Button>
              </div>
              {companyData.profit_margin > 0 ? (
                <p className="text-xs text-emerald-600 mt-3" data-testid="profit-margin-example">
                  {t('profitMarginExample').replace('{amount}', (1000 * (1 + companyData.profit_margin / 100)).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) + ' ₺')}
                </p>
              ) : (
                <p className="text-xs text-slate-500 mt-3">{t('noProfitMargin')}</p>
              )}
            </CardContent>
          </Card>

          {/* Password Change Section */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {t('changePassword')}
              </CardTitle>
              <CardDescription>
                {t('passwordDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="currentPassword">{t('currentPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      placeholder="••••••••"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="newPassword">{t('newPassword')}</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        placeholder="••••••••"
                        required
                        minLength={6}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showNewPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">{t('confirmNewPassword')}</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        placeholder="••••••••"
                        required
                        minLength={6}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showConfirmPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500">En az 6 karakter</p>
                <Button 
                  type="submit" 
                  disabled={passwordLoading}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {passwordLoading ? t('saving') : t('updatePassword')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Account Section */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {t('accountInfoTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-4 border-amber-200" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-xl font-bold">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-lg text-slate-900">{user?.name}</p>
                  <p className="text-slate-500">{user?.email}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="outline" 
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {t('logout')}
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="forget-me-btn"
                  onClick={() => {
                    if (!window.confirm(
                      t('forgetMeConfirm')
                    )) return;
                    window.open("https://accounts.google.com/Logout", "_blank");
                    localStorage.clear();
                    sessionStorage.clear();
                    logout();
                  }}
                >
                  {t('forgetMeBtn')}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-3">
                {t('forgetMeNote')}
              </p>
            </CardContent>
          </Card>

          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
